import { type FC } from 'react';
import cn from 'classnames';
import { HStack, Link, Text, VStack } from '@direct-frontend/components';
import { formatPhoneNumber } from '../../lib/formatPhoneNumber';
import { makeModifierClassname } from '../../lib/makeModifierClassname';
import { getRequestContext } from '../../lib/requestContext';
import { CompanyLogo } from '../CompanyLogo';
import styles from './Footer.module.css';

const footerModifier = makeModifierClassname(styles, 'footer');

interface FooterProps {
    centered?: Boolean;
    hideOnTouch?: boolean;
    hideOnDesktop?: boolean;
    className?: string;
}

export const Footer: FC<FooterProps> = ({ centered, hideOnTouch, hideOnDesktop, className }) => {
    const {
        company: {
            companyShopUrl,
            companyDeliveryPath,
            companyPaymentPath,
            companyReturnPath,
            companyLogo,
            phones,
            addresses,
        },
        isDigitalOrder,
    } = getRequestContext();

    if (!companyShopUrl) {
        return null;
    }

    const phone = phones?.find((phone) => phone.visibility === 'PUBLISHED') || phones ? phones[0] : null;

    return (
        <VStack
            alignItems={centered ? 'center' : 'flex-start'}
            gap="12"
            className={cn(
                footerModifier.className,
                className,
                footerModifier('hideOnTouch', hideOnTouch),
                footerModifier('hideOnDesktop', hideOnDesktop)
            )}
        >
            <HStack gap="24" className={styles.links}>
                <HStack {...{ as: Link, href: `${companyShopUrl}${companyPaymentPath}`, target: '_blank' }}>
                    <Text size="mc">Оплата</Text>
                </HStack>
                {!isDigitalOrder && (
                    <>
                        <HStack {...{ as: Link, href: `${companyShopUrl}${companyDeliveryPath}`, target: '_blank' }}>
                            <Text size="mc">Доставка</Text>
                        </HStack>
                        <HStack {...{ as: Link, href: `${companyShopUrl}${companyReturnPath}`, target: '_blank' }}>
                            <Text size="mc">Правила возврата</Text>
                        </HStack>
                    </>
                )}
            </HStack>

            <div className={styles.divider} />

            <VStack alignItems={centered ? 'center' : 'flex-start'} gap="12">
                <Link href={companyShopUrl}>
                    <CompanyLogo logo={companyLogo} position="footer" alt="Логотип" />
                </Link>

                <VStack alignItems={centered ? 'center' : 'unset'} gap="6">
                    {addresses && addresses?.length > 0 ? (
                        <Text
                            size="mc"
                            role="placeholder"
                            color="gray-disabled"
                            textAlign={centered ? 'center' : 'left'}
                        >
                            {addresses[0].value}
                        </Text>
                    ) : null}
                    {phone ? (
                        <Link href={`tel:${phone.value}`}>
                            <Text size="mc" role="placeholder">
                                {formatPhoneNumber(phone.value)}
                            </Text>
                        </Link>
                    ) : null}
                </VStack>
            </VStack>
        </VStack>
    );
};
